import * as React from 'react';
import type { DrawerProps } from '@fluentui/react-components';
import { makeStyles, OverlayDrawer } from '@fluentui/react-components';
import { useMediaQuery } from '@react-hook/media-query';
import { screenSizes } from '../modules/breakpoints';

interface DrawerContext {
  showDrawer: boolean;
  setShowDrawer: (show: boolean | ((shown: boolean) => boolean)) => void;
  children: React.ReactNode | null;
  setChildren: (Component: React.ReactNode | null, size?: DrawerProps['size']) => void;
  drawerSize?: DrawerProps['size'];
}

export const DrawerContext = React.createContext<DrawerContext>({
  showDrawer: false,
  setShowDrawer: () => {},
  setChildren: () => {},
  children: null,
  drawerSize: undefined,
});

export function DrawerProvider({ children }: { children: React.ReactNode }) {
  const [showDrawer, setShowDrawer] = React.useState(false);
  const [drawerChildren, setChildren] = React.useState<React.ReactNode>(null);
  const [drawerSize, setSize] = React.useState<DrawerProps['size']>(undefined);

  return (
    <DrawerContext.Provider
      value={{
        showDrawer,
        setShowDrawer,
        setChildren: (children: React.ReactNode, drawerSize?: DrawerProps['size']) => {
          setChildren(children);
          setSize(drawerSize || 'medium');
        },
        drawerSize,
        children: drawerChildren,
      }}
    >
      {children}
    </DrawerContext.Provider>
  );
}

export function FoundersHubUserOverlayDrawer() {
  const classes = useStyles();
  const isSmallScreen = useMediaQuery(`screen and (max-width: ${screenSizes.xs})`);
  const [topOffset, setTopOffset] = React.useState(0);

  const handleScroll = () => {
    if (document.getElementsByTagName('header').length === 0) {
      setTopOffset(0);
      return;
    }

    const headerHeight = document.getElementsByTagName('header')[0].clientHeight ?? 0;
    const headerTopY = document.getElementsByTagName('header')[0].getBoundingClientRect().top ?? 0;
    const offset = headerTopY > -1 * headerHeight ? headerTopY + headerHeight : 0;
    setTopOffset(offset);
  };

  React.useEffect(() => {
    window.addEventListener('resize', handleScroll, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('resize', handleScroll);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const responsiveWidth = isSmallScreen ? '100%' : '420px';
  const { children, showDrawer, drawerSize } = React.useContext(DrawerContext);

  return (
    <OverlayDrawer
      position="end"
      open={showDrawer}
      style={{
        ...(drawerSize && !isSmallScreen ? {} : { width: 'auto' }),
        ...(drawerSize && !isSmallScreen ? {} : { minWidth: responsiveWidth }),
        top: `${topOffset}px`,
        height: `calc(100vh - ${topOffset}px)`,
      }}
      className={classes.drawer}
      backdrop={{ className: classes.backdrop, style: { top: `${topOffset}px` } }}
      size={drawerSize}
    >
      {children}
    </OverlayDrawer>
  );
}

const useStyles = makeStyles({
  drawer: {
    zIndex: 1e14,
  },
  backdrop: {
    position: 'fixed',
    zIndex: 1e13,
  },
});
